import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserProfile } from '../types';
import {
  auth,
  getUserProfileFromFirestore,
  syncUserProfileToFirestore,
  signOutUser,
  subscribeToUserProfile,
  signInWithGoogle,
} from '../lib/firebase';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { getDefaultCompanionServices } from '../data/servicesData';

export const DEFAULT_USER_PROFILE: UserProfile = {
  name: '',
  legalName: '',
  displayName: '',
  phone: '',
  email: '',
  avatarUrl: '',
  gallery: [],
  services: getDefaultCompanionServices(1500),
  role: 'Verified Client',
  accountType: 'client',
  companion_profile_status: 'Inactive',
  membership_status: 'INACTIVE',
  payment_status: 'Pending',
  payment_verified: false,
  city: '',
  bio: '',
  hourlyRate: 1500,
  rateOneThirdDay: 2000,
  rateHalfDay: 3000,
  rateFullDay: 5000,
  drivingLicenceStatus: 'NOT_UPLOADED',
  isDriverApproved: false,
  isIdVerified: false,
  upiId: '',
  bankAccount: '',
  membership: {
    plan: 'STARTER',
    price: 0,
    status: 'PENDING',
    nextBillingDate: '',
    paymentHistory: [],
  },
  wallet: {
    availableBalance: 0,
    totalEarned: 0,
    pendingEscrow: 0,
    completedMeetups: 0,
    transactions: [],
  },
};

export const sanitizeUserProfile = (raw: any): UserProfile => {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_USER_PROFILE };
  }

  const sanitized: UserProfile = {
    name: raw.name || raw.displayName || DEFAULT_USER_PROFILE.name,
    displayName: raw.displayName || raw.name || DEFAULT_USER_PROFILE.displayName,
    legalName: raw.legalName || raw.name || DEFAULT_USER_PROFILE.legalName,
    phone: raw.phone || DEFAULT_USER_PROFILE.phone,
    email: raw.email || DEFAULT_USER_PROFILE.email,
    avatarUrl: raw.avatarUrl || DEFAULT_USER_PROFILE.avatarUrl,
    gallery: Array.isArray(raw.gallery) && raw.gallery.length > 0 ? raw.gallery : DEFAULT_USER_PROFILE.gallery,
    services: Array.isArray(raw.services) && raw.services.length > 0 ? raw.services : getDefaultCompanionServices(raw.hourlyRate || 1500),
    role: raw.role || (raw.accountType === 'client' ? 'Verified Client' : 'Companion Host & Member'),
    accountType: raw.accountType || (raw.companionProfile ? 'companion' : 'client'),
    companionStatus: raw.companionStatus || 'REGISTERED',
    companion_profile_status: raw.companion_profile_status || 'Inactive',
    membership_status: raw.membership_status ? (raw.membership_status.toUpperCase() === 'ACTIVE' ? 'ACTIVE' : raw.membership_status.toUpperCase() === 'PENDING' ? 'PENDING' : raw.membership_status.toUpperCase() === 'CANCELLED' ? 'CANCELLED' : 'INACTIVE') : 'INACTIVE',
    payment_status: raw.payment_status || 'Pending',
    payment_verified: raw.payment_verified === true,
    city: raw.city || 'Kolkata, West Bengal',
    bio: raw.bio || 'Verified member on PairUpToday.',
    hourlyRate: Math.max(600, Number(raw.hourlyRate) || 1500),
    rateOneThirdDay: Number(raw.rateOneThirdDay) || 2000,
    rateHalfDay: Number(raw.rateHalfDay) || 3000,
    rateFullDay: Number(raw.rateFullDay) || 5000,
    drivingLicenceStatus: raw.drivingLicenceStatus || 'NOT_UPLOADED',
    drivingLicenceDetails: raw.drivingLicenceDetails || DEFAULT_USER_PROFILE.drivingLicenceDetails,
    isDriverApproved: raw.isDriverApproved ?? (raw.drivingLicenceStatus === 'APPROVED'),
    isIdVerified: raw.isIdVerified === true,
    upiId: raw.upiId || '',
    bankAccount: raw.bankAccount || '',
    membership: raw.membership || { ...DEFAULT_USER_PROFILE.membership, paymentHistory: [] },
    clientProfile: raw.clientProfile,
    companionProfile: raw.companionProfile,
    wallet: {
      availableBalance: Number(raw.wallet?.availableBalance) || 0,
      totalEarned: Number(raw.wallet?.totalEarned) || 0,
      pendingEscrow: Number(raw.wallet?.pendingEscrow) || 0,
      completedMeetups: Number(raw.wallet?.completedMeetups) || 0,
      transactions: Array.isArray(raw.wallet?.transactions) ? raw.wallet.transactions : [],
    },
  };

  return sanitized;
};

interface AuthContextType {
  currentUser: string | null;
  userProfile: UserProfile | null;
  accountType: 'client' | 'companion';
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (name: string, type?: 'client' | 'companion', profile?: Partial<UserProfile>, membership?: any) => void;
  loginWithGoogle: (type?: 'client' | 'companion') => Promise<void>;
  logout: () => void;
  updateProfile: (partial: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<string | null>(() => {
    try {
      return localStorage.getItem('pairup_user');
    } catch {
      return null;
    }
  });

  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    try {
      const saved = localStorage.getItem('pairup_user_profile_data');
      if (saved) {
        return sanitizeUserProfile(JSON.parse(saved));
      }
      return null;
    } catch {
      return null;
    }
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Sync state with Firebase auth state and real-time Firestore listener
  useEffect(() => {
    let isMounted = true;
    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      // Clean up any previous profile listener
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      try {
        if (firebaseUser) {
          // 1. Initial snapshot check
          const cloudProfile = await getUserProfileFromFirestore(firebaseUser.uid);
          if (cloudProfile && isMounted) {
            const sanitized = sanitizeUserProfile(cloudProfile);
            setUserProfile(sanitized);
            const userIdentifier = sanitized.displayName || sanitized.name || firebaseUser.email || 'Member';
            setCurrentUser(userIdentifier);
            localStorage.setItem('pairup_user', userIdentifier);
            localStorage.setItem('pairup_user_profile_data', JSON.stringify(sanitized));
          } else if (isMounted && !userProfile) {
            const localSaved = localStorage.getItem('pairup_user_profile_data');
            if (localSaved) {
              setUserProfile(sanitizeUserProfile(JSON.parse(localSaved)));
            }
          }

          // 2. Attach REAL-TIME listener to user's Firestore document
          unsubscribeProfile = subscribeToUserProfile(firebaseUser.uid, (updatedCloudProfile) => {
            if (updatedCloudProfile && isMounted) {
              const sanitized = sanitizeUserProfile(updatedCloudProfile);
              setUserProfile(sanitized);
              const userIdentifier = sanitized.displayName || sanitized.name || firebaseUser.email || 'Member';
              setCurrentUser(userIdentifier);
              localStorage.setItem('pairup_user', userIdentifier);
              localStorage.setItem('pairup_user_profile_data', JSON.stringify(sanitized));
            }
          });
        }
      } catch (err: any) {
        console.warn('Auth state sync notice:', err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    });

    const timer = setTimeout(() => {
      if (isMounted) setIsLoading(false);
    }, 500);

    return () => {
      isMounted = false;
      if (unsubscribeProfile) unsubscribeProfile();
      unsubscribeAuth();
      clearTimeout(timer);
    };
  }, []);

  const login = (
    name: string,
    type: 'client' | 'companion' = 'client',
    profile?: Partial<UserProfile>,
    membership?: any
  ) => {
    try {
      const base = profile
        ? sanitizeUserProfile({ ...profile, accountType: type })
        : { ...DEFAULT_USER_PROFILE, name, displayName: name, accountType: type };
      if (membership) {
        base.membership = membership;
      }
      setCurrentUser(name);
      setUserProfile(base);
      localStorage.setItem('pairup_user', name);
      localStorage.setItem('pairup_user_profile_data', JSON.stringify(base));

      if (auth.currentUser) {
        syncUserProfileToFirestore({
          uid: auth.currentUser.uid,
          ...base,
        } as any).catch(console.warn);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
    }
  };

  const loginWithGoogle = async (type: 'client' | 'companion' = 'client') => {
    setIsLoading(true);
    setError(null);
    try {
      const { user, profile } = await signInWithGoogle(type);
      const sanitized = sanitizeUserProfile({
        ...profile,
        accountType: type,
      });
      setUserProfile(sanitized);
      const identifier = sanitized.displayName || sanitized.name || user.email || 'Google User';
      setCurrentUser(identifier);
      localStorage.setItem('pairup_user', identifier);
      localStorage.setItem('pairup_user_profile_data', JSON.stringify(sanitized));
    } catch (err: any) {
      console.warn('Google login notice:', err);
      // Don't treat popup-closed by user or cancelled requests as fatal crashes
      if (
        err?.code === 'auth/popup-closed-by-user' ||
        err?.code === 'auth/cancelled-popup-request' ||
        err?.message?.includes('popup-closed-by-user')
      ) {
        setError(null);
        return;
      }
      setError(err?.message || 'Google sign-in failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    try {
      setCurrentUser(null);
      setUserProfile(null);
      localStorage.removeItem('pairup_user');
      localStorage.removeItem('pairup_user_profile_data');
      signOutUser().catch(console.warn);
    } catch (err: any) {
      console.error('Logout error:', err);
    }
  };

  const updateProfile = async (partial: Partial<UserProfile>) => {
    try {
      const current = userProfile || DEFAULT_USER_PROFILE;
      const updated = sanitizeUserProfile({ ...current, ...partial });
      setUserProfile(updated);
      if (updated.displayName || updated.name) {
        setCurrentUser(updated.displayName || updated.name);
        localStorage.setItem('pairup_user', updated.displayName || updated.name);
      }
      localStorage.setItem('pairup_user_profile_data', JSON.stringify(updated));

      if (auth.currentUser) {
        await syncUserProfileToFirestore({
          uid: auth.currentUser.uid,
          ...updated,
        } as any);
      }
    } catch (err: any) {
      console.error('Update profile error:', err);
      setError(err.message || 'Failed to update profile');
      throw err;
    }
  };

  const refreshProfile = async () => {
    try {
      setError(null);
      if (auth.currentUser) {
        const cloud = await getUserProfileFromFirestore(auth.currentUser.uid);
        if (cloud) {
          const sanitized = sanitizeUserProfile(cloud);
          setUserProfile(sanitized);
          localStorage.setItem('pairup_user_profile_data', JSON.stringify(sanitized));
          return;
        }
      }
      const localSaved = localStorage.getItem('pairup_user_profile_data');
      if (localSaved) {
        setUserProfile(sanitizeUserProfile(JSON.parse(localSaved)));
      }
    } catch (err: any) {
      console.error('Refresh profile error:', err);
      setError(err.message || 'Failed to refresh profile');
    }
  };

  const accountType: 'client' | 'companion' =
    userProfile?.accountType || (userProfile?.companionProfile ? 'companion' : 'client');

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userProfile,
        accountType,
        isAuthenticated: !!currentUser,
        isLoading,
        error,
        login,
        loginWithGoogle,
        logout,
        updateProfile,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

